import * as CL from '@design-system/component-library';
import { type JSX, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { myOrdersMsg, t } from '../../common/i18n';
import { getConfiguredCommercialProducts } from '../../common/utils/cartProductUtils';
import { formatSum } from '../../common/utils/priceUtils';
import { DeliveryType } from '../../generated/api/deliveryType.js';
import { OnlineOrder } from '../../generated/api/onlineOrder.js';
import { useOnlineModelsContext } from '../../public/site/onlineModel/onlineModelContextProvider';
import { clearCartItems, clearDeliveryDetails } from '../../selfservice/actions';
import { DeviceCheckoutListingItem } from '../DeviceCheckout/DeviceCheckoutListingItem';
import { isCartItemBackordered } from '../DeviceCheckout/deviceCheckoutUtils';
import { DeviceCheckoutCartProduct } from '../DeviceCheckoutCartProduct/DeviceCheckoutCartProduct';
import { OrderNewsLetter } from '../OrderNewsLetter/OrderNewsLetter';
import { OrderSummary, OrderSummaryType } from '../OrderSummary/OrderSummary';
import type { ShoppingCartItemForCheckout } from '../../common/types/checkout';
import type { CompanyInfoResponse, ContactPerson, OnlineModel } from '../../generated/api/models';
import type { DeviceCheckoutDeliveryDetailsType } from '../DeviceCheckoutDeliveryDetails/DeviceCheckoutDeliveryDetailsUtils';

import './EoeDeviceCheckoutThankYou.scss';

export interface EoeDeviceCheckoutThankYouProps {
  cartItems: Array<ShoppingCartItemForCheckout>;
  companyInfo?: CompanyInfoResponse;
  contactInfo: ContactPerson;
  deliveryDetails: DeviceCheckoutDeliveryDetailsType;
  onlineModels?: OnlineModel[];
}

interface EoeThankYouDeliveryDetailsProps {
  deliveryDetails: DeviceCheckoutDeliveryDetailsType;
  shipmentType?: OnlineOrder.ShipmentTypeEnum;
}

const isDeliveryToAddress = (deliveryMethodType: DeliveryType) =>
  [
    DeliveryType.MATKAHUOLTO_JAKOPAKETTI,
    DeliveryType.POSTI_EXPRESS_BUSINESS,
    DeliveryType.POSTI_EXPRESS_MORNING,
  ].includes(deliveryMethodType);

const ThankYouDeliveryDetails = ({ deliveryDetails, shipmentType }: EoeThankYouDeliveryDetailsProps) => {
  const isDelivery = isDeliveryToAddress(deliveryDetails.deliveryMethodType);
  return (
    <DeviceCheckoutListingItem
      description={
        <>
          <div className={dsClass.TEXT_L}>{deliveryDetails.deliveryMethodDescription}</div>
          {shipmentType === OnlineOrder.ShipmentTypeEnum.TOTAL_DELIVERY && (
            <div className={dsClass.MARGIN_TOP_3}>
              <div className={dsClass.TEXT_L}>{t.BSE3('Delivery')}:</div>
              <div>{t.LMKE('When all products are available')}</div>
            </div>
          )}
        </>
      }
      image={<CL.Icon icon={isDelivery ? 'delivery' : 'location'} size="xl" type="light" />}
      price={<div className={dsClass.H4}>{formatSum(deliveryDetails.deliveryPrice)}</div>}
    />
  );
};

// The form was substantially narrower than the max allowed, so this keeps it
// that way. Actual full width would be 12.
const FULL_WIDTH_ON_DESKTOP = 8;

const HALF_WIDTH_ON_DESKTOP_FULL_WIDTH_ON_OTHERS = {
  colWidthXS: 4,
  colWidthS: 6,
  colWidthM: 3,
  colWidthL: 4,
  colWidthXL: 4,
} as const;

const ThankYouHeading = ({ contactInfo }: Pick<EoeDeviceCheckoutThankYouProps, 'contactInfo'>) => (
  <CL.GridRow justifyCenter>
    <CL.GridCol className={dsClass.GRID_COL} colWidth={FULL_WIDTH_ON_DESKTOP}>
      <h1 className={dsClass.H2}>{t.O4R6('Thank you for your order!')}</h1>
      <p>
        {`${t.IT7Q(
          'Your order has been submitted. If your company has allowed receiving order confirmations, you will get a notification to your {} address soon.',
          contactInfo.email
        )} `}
        <br />
        {t.CC7C('You can track your orders on ')}
        <a href={paths.EMPLOYEE_OWN_ORDERS}>{t.EG6F(myOrdersMsg)}</a>
        {t.X1DF(' page.')}
      </p>
    </CL.GridCol>
  </CL.GridRow>
);

const ThankYouContactInfo = ({
  companyInfo,
  orderDeliveryDetails,
}: {
  companyInfo?: CompanyInfoResponse;
  orderDeliveryDetails: DeviceCheckoutDeliveryDetailsType;
}) => (
  <>
    <CL.GridRow justifyCenter>
      <CL.GridCol className={`${dsClass.GRID_COL} ${dsClass.MARGIN_TOP_3}`} colWidth={FULL_WIDTH_ON_DESKTOP}>
        <h2 className={`of-device-checkout-thankyou__company-details--heading ${dsClass.H3}`}>
          {t.HS3T('Customer details')}
        </h2>
      </CL.GridCol>
    </CL.GridRow>
    <CL.GridRow justifyCenter>
      <CL.GridCol
        className={`${dsClass.GRID_COL} ${dsClass.MARGIN_TOP_3}`}
        {...HALF_WIDTH_ON_DESKTOP_FULL_WIDTH_ON_OTHERS}
      >
        <div>{orderDeliveryDetails.companyName || ''}</div>
        <div>{orderDeliveryDetails.recipientName}</div>
        <div>{orderDeliveryDetails.recipientPhoneNumber}</div>
      </CL.GridCol>
      <CL.GridCol
        className={`${dsClass.GRID_COL} ${dsClass.MARGIN_TOP_3}`}
        {...HALF_WIDTH_ON_DESKTOP_FULL_WIDTH_ON_OTHERS}
      >
        {orderDeliveryDetails.pickupPoint?.address ? (
          <div className="of-device-checkout-thankyou__delivery-details--address">
            <div>{orderDeliveryDetails.pickupPoint.name}</div>
            <div>{orderDeliveryDetails.pickupPoint.address}</div>
          </div>
        ) : (
          orderDeliveryDetails.address && (
            <div>
              <div>{orderDeliveryDetails.address.line1}</div>
              <div>{orderDeliveryDetails.address.line2 || ''}</div>
              <div>
                {orderDeliveryDetails.address.postalCode}, {orderDeliveryDetails.address.postOffice}
              </div>
            </div>
          )
        )}
        <div>
          {t.MPA5('Business Id')} {companyInfo?.businessId}
        </div>
      </CL.GridCol>
    </CL.GridRow>
  </>
);

const ThankYouProductsOrdered = ({
  cartItemsState,
  onlineModels,
  orderDeliveryDetails,
}: {
  cartItemsState: ShoppingCartItemForCheckout[];
  onlineModels?: OnlineModel[];
  orderDeliveryDetails: DeviceCheckoutDeliveryDetailsType;
}) => (
  <>
    <CL.GridRow justifyCenter>
      <CL.GridCol className={`${dsClass.GRID_COL} ${dsClass.MARGIN_TOP_3}`} colWidth={FULL_WIDTH_ON_DESKTOP}>
        <h2 className={`of-device-checkout-thankyou__item-details--heading ${dsClass.H3}`}>
          {t.OT3E('Products ordered')}
        </h2>
      </CL.GridCol>
    </CL.GridRow>
    {cartItemsState.map((cartItem, index) => {
      return (
        <CL.GridRow justifyCenter key={cartItem.offerCode + index}>
          <CL.GridCol colWidth={FULL_WIDTH_ON_DESKTOP}>
            {/*
            This div only exists so that we have the correct border width...
            */}
            <div className="of-device-checkout-thankyou__product-list--bordered">
              <DeviceCheckoutCartProduct
                cartItem={cartItem}
                getCartItemDomRefFns={() => {}}
                getAdditionalParametersFns={() => {}}
                setGetContactOrPurposeOfUseFn={() => {}}
                onUpdateDeviceChangeSelection={() => {}}
                readOnlyMode={true}
                isCartItemBackordered={
                  isCartItemBackordered(cartItem.offerCode, onlineModels) &&
                  orderDeliveryDetails.shipmentType === OnlineOrder.ShipmentTypeEnum.PARTIAL_DELIVERY
                }
              />
            </div>
          </CL.GridCol>
        </CL.GridRow>
      );
    })}
  </>
);

export const EoeDeviceCheckoutThankYou = ({
  cartItems,
  companyInfo,
  contactInfo,
  deliveryDetails,
  onlineModels,
}: EoeDeviceCheckoutThankYouProps): JSX.Element => {
  const dispatch = useDispatch();
  const [cartItemsState] = useState(cartItems);
  const methods = useForm();
  const [orderDeliveryDetails] = useState(deliveryDetails);
  const { setActiveVoucher } = useOnlineModelsContext();

  useEffect(() => {
    // Clear the basket after successful order.
    dispatch(clearCartItems());
    dispatch(clearDeliveryDetails());
    setActiveVoucher('');
  }, [dispatch]); /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps

  // TODO: Update the image of delivery cart, when available
  const cartItemsCommercialProducts = getConfiguredCommercialProducts(cartItemsState);

  return (
    <FormProvider {...methods}>
      <form>
        <CL.Grid className={dsClass.GRID}>
          <ThankYouHeading contactInfo={contactInfo} />
          <ThankYouContactInfo companyInfo={companyInfo} orderDeliveryDetails={orderDeliveryDetails} />
          <ThankYouProductsOrdered
            cartItemsState={cartItemsState}
            onlineModels={onlineModels}
            orderDeliveryDetails={orderDeliveryDetails}
          />
          <ThankYouDeliveryDetails
            shipmentType={orderDeliveryDetails.shipmentType}
            deliveryDetails={orderDeliveryDetails}
          />
          <CL.GridRow justifyCenter>
            <CL.GridCol className={dsClass.GRID_COL} colWidth={FULL_WIDTH_ON_DESKTOP}>
              <OrderSummary
                priceIncludesVAT={true}
                summaryType={OrderSummaryType.DETAILS_CLOSED}
                commercialProducts={cartItemsCommercialProducts}
                deliveryCharges={orderDeliveryDetails.deliveryPrice}
              />
            </CL.GridCol>
          </CL.GridRow>
          <CL.GridRow justifyCenter>
            <CL.GridCol className={dsClass.GRID_COL} colWidth={FULL_WIDTH_ON_DESKTOP}>
              <OrderNewsLetter />
            </CL.GridCol>
          </CL.GridRow>
        </CL.Grid>
      </form>
    </FormProvider>
  );
};
